import React, {useMemo, useState} from "react";
import {Box, Flex, Text, useColorModeValue} from "@chakra-ui/react";
import {buildProjection, featureCentroid, featureToPath} from "../utils/districts";
import "../businessReport.css";

// Renders the Nepal district boundaries as an SVG choropleth. Districts that
// host bell clients are shaded by client count and marked with a pulsing
// pointer; hovering a district (or its marker) shows a tooltip.
const EMPTY = {};

const NepalDistrictMap = ({geojson, stats = EMPTY, onSelectDistrict, selectedDistrict}) => {
    const baseFill = useColorModeValue("#EDF2F7", "#2D3748");
    const strokeColor = useColorModeValue("#CBD5E0", "#4A5568");
    const tooltipBg = useColorModeValue("white", "gray.700");
    const tooltipColor = useColorModeValue("gray.800", "white");

    const [hover, setHover] = useState(null); // {district, x, y}

    const {paths, markers, viewBox} = useMemo(() => {
        if (!geojson || !geojson.features) {
            return {paths: [], markers: [], viewBox: "0 0 1000 700"};
        }
        const {project, width, height} = buildProjection(geojson.features, 1000);
        const maxCount = Object.values(stats).reduce((m, s) => Math.max(m, s.count || 0), 0) || 1;

        const paths = geojson.features.map((f) => {
            const name = (f.properties.DISTRICT || "").toUpperCase();
            const stat = stats[name];
            return {name, d: featureToPath(f, project), count: stat ? stat.count : 0};
        });

        const markers = geojson.features
            .map((f) => {
                const name = (f.properties.DISTRICT || "").toUpperCase();
                const stat = stats[name];
                if (!stat || !stat.count) return null;
                const c = featureCentroid(f, project);
                if (!c) return null;
                const r = 5 + Math.min(14, (stat.count / maxCount) * 14);
                return {name, x: c[0], y: c[1], r, count: stat.count};
            })
            .filter(Boolean);

        return {paths, markers, viewBox: `0 0 ${width} ${Math.round(height)}`, maxCount};
    }, [geojson, stats]);

    const fillFor = (name, count) => {
        if (name === selectedDistrict) return "#2B6CB0";
        if (!count) return baseFill;
        const maxCount = Object.values(stats).reduce((m, s) => Math.max(m, s.count || 0), 0) || 1;
        const t = Math.min(1, count / maxCount);
        // Light -> strong blue ramp.
        const shades = ["#BEE3F8", "#90CDF4", "#63B3ED", "#4299E1", "#3182CE"];
        return shades[Math.min(shades.length - 1, Math.floor(t * (shades.length - 1)))];
    };

    const handleMove = (e, name) => {
        const rect = e.currentTarget.ownerSVGElement.getBoundingClientRect();
        setHover({district: name, x: e.clientX - rect.left, y: e.clientY - rect.top});
    };

    const hoveredStat = hover ? stats[hover.district] : null;

    return (
        <Box position="relative" w="100%">
            <svg
                viewBox={viewBox}
                width="100%"
                height="auto"
                preserveAspectRatio="xMidYMid meet"
                role="img"
                aria-label="Nepal district map of bell clients"
                style={{display: "block"}}
            >
                {paths.map((p) => (
                    <path
                        key={p.name}
                        className="br-district"
                        d={p.d}
                        fill={fillFor(p.name, p.count)}
                        stroke={strokeColor}
                        strokeWidth={0.6}
                        onMouseEnter={(e) => handleMove(e, p.name)}
                        onMouseMove={(e) => handleMove(e, p.name)}
                        onMouseLeave={() => setHover(null)}
                        onClick={() => onSelectDistrict && onSelectDistrict(p.count ? p.name : null)}
                    />
                ))}

                {markers.map((m) => (
                    <g
                        key={`m-${m.name}`}
                        onMouseEnter={(e) => handleMove(e, m.name)}
                        onMouseMove={(e) => handleMove(e, m.name)}
                        onMouseLeave={() => setHover(null)}
                        onClick={() => onSelectDistrict && onSelectDistrict(m.name)}
                        style={{cursor: "pointer"}}
                    >
                        <circle className="br-marker-halo" cx={m.x} cy={m.y} r={m.r} fill="#E53E3E"/>
                        <circle cx={m.x} cy={m.y} r={m.r * 0.6} fill="#C53030" stroke="white" strokeWidth={1.2}/>
                    </g>
                ))}
            </svg>

            {hover && (
                <Box
                    position="absolute"
                    left={`${hover.x + 14}px`}
                    top={`${hover.y + 12}px`}
                    bg={tooltipBg}
                    color={tooltipColor}
                    boxShadow="lg"
                    borderRadius="8px"
                    px="12px"
                    py="8px"
                    pointerEvents="none"
                    zIndex={5}
                    maxW="220px"
                >
                    <Text fontSize="sm" fontWeight="bold" textTransform="capitalize">
                        {hover.district.toLowerCase()}
                    </Text>
                    <Text fontSize="xs" color={hoveredStat ? "blue.400" : "gray.400"}>
                        {hoveredStat ? `${hoveredStat.count} bell client${hoveredStat.count > 1 ? "s" : ""}` : "No bell clients"}
                    </Text>
                    {hoveredStat && hoveredStat.schools && (
                        <Box mt="4px">
                            {hoveredStat.schools.slice(0, 4).map((s, i) => (
                                <Text key={i} fontSize="xs" color="gray.500" isTruncated>
                                    • {s.name}
                                </Text>
                            ))}
                            {hoveredStat.schools.length > 4 && (
                                <Text fontSize="xs" color="gray.400">
                                    +{hoveredStat.schools.length - 4} more
                                </Text>
                            )}
                        </Box>
                    )}
                </Box>
            )}

            {/* Legend */}
            <Flex mt="12px" gap="16px" align="center" wrap="wrap">
                <Flex align="center" gap="6px">
                    <Box w="14px" h="14px" borderRadius="3px" bg="#3182CE"/>
                    <Text fontSize="xs" color="gray.500">More clients</Text>
                </Flex>
                <Flex align="center" gap="6px">
                    <Box w="14px" h="14px" borderRadius="3px" bg="#BEE3F8"/>
                    <Text fontSize="xs" color="gray.500">Fewer clients</Text>
                </Flex>
                <Flex align="center" gap="6px">
                    <Box w="14px" h="14px" borderRadius="3px" bg={baseFill} border="1px solid" borderColor={strokeColor}/>
                    <Text fontSize="xs" color="gray.500">No clients</Text>
                </Flex>
                <Flex align="center" gap="6px">
                    <Box w="12px" h="12px" borderRadius="full" bg="#C53030" border="2px solid white" boxShadow="0 0 0 1px #C53030"/>
                    <Text fontSize="xs" color="gray.500">Client location</Text>
                </Flex>
            </Flex>
        </Box>
    );
};

export default NepalDistrictMap;
